import type { fabric } from 'fabric';
import React, { useCallback } from 'react';
import { registerInspector } from './inspector-registry';
import PlaceholderTransformSection from './placeholder-transform-section';
import ColorField from './shared/color-field';
import CommitNumberField from './shared/commit-number-field';
import CommitTextField from './shared/commit-text-field';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import PlaceholderIdentitySection from './shared/placeholder-identity-section';
import SelectField from './shared/select-field';
import { useFabricData } from './shared/use-fabric-data';
import type { ObjectInspectorProps } from './types';

const FONT_FAMILIES = [
  { value: 'Arial', label: 'Arial' },
  { value: 'Helvetica', label: 'Helvetica' },
  { value: 'Times New Roman', label: 'Times New Roman' },
  { value: 'Georgia', label: 'Georgia' },
  { value: 'Courier New', label: 'Courier New' },
  { value: 'Verdana', label: 'Verdana' },
];

const FONT_WEIGHTS = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: '300', label: 'Light (300)' },
  { value: '500', label: 'Medium (500)' },
  { value: '700', label: 'Bold (700)' },
];

const TEXT_ALIGNS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const PlaceholderTextInspector: React.FC<ObjectInspectorProps> = ({ object, editor }) => {
  const [previewMode, setPreviewMode] = useFabricData<boolean>(object, 'previewMode', editor);
  const [sampleText, setSampleText] = useFabricData<string>(object, 'sampleText', editor);
  const [fontFamily, setFontFamily] = useFabricData<string>(object, 'fontFamily', editor);
  const [fontWeight, setFontWeight] = useFabricData<string>(object, 'fontWeight', editor);
  const [textAlign, setTextAlign] = useFabricData<string>(object, 'textAlign', editor);
  const [fill, setFill] = useFabricData<string>(object, 'fill', editor);
  const [minFontSize, setMinFontSize] = useFabricData<number>(object, 'minFontSize', editor);
  const [maxFontSize, setMaxFontSize] = useFabricData<number>(object, 'maxFontSize', editor);

  const refit = useCallback(() => {
    editor.placeholders?.refit(object as fabric.Group);
  }, [editor, object]);

  return (
    <>
      <InspectorSection title="Preview">
        <InspectorRow label="Preview mode">
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={Boolean(previewMode)}
              onChange={(e) => {
                setPreviewMode(e.target.checked || undefined);
                refit();
              }}
            />
            <span>show only sample text</span>
          </CheckboxLabel>
        </InspectorRow>
      </InspectorSection>
      <PlaceholderIdentitySection object={object} editor={editor} />
      <InspectorSection title="Sample text">
        <InspectorRow label="Text">
          <CommitTextField
            value={sampleText}
            onCommit={(v) => {
              setSampleText(v);
              refit();
            }}
            multiline
            placeholder="Preview text"
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Typography">
        <InspectorRow label="Family">
          <SelectField
            value={fontFamily ?? 'Arial'}
            onChange={(v) => {
              setFontFamily(v);
              refit();
            }}
            options={FONT_FAMILIES}
          />
        </InspectorRow>
        <InspectorRow label="Weight">
          <SelectField
            value={String(fontWeight ?? 'normal')}
            onChange={(v) => {
              setFontWeight(v);
              refit();
            }}
            options={FONT_WEIGHTS}
          />
        </InspectorRow>
        <InspectorRow label="Align">
          <SelectField
            value={textAlign ?? 'center'}
            onChange={(v) => {
              setTextAlign(v);
              refit();
            }}
            options={TEXT_ALIGNS}
          />
        </InspectorRow>
        <InspectorRow label="Color">
          <ColorField
            value={fill ?? '#111827'}
            onChange={(v) => {
              setFill(v);
              refit();
            }}
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Fit">
        <InspectorRow label="Min size">
          <CommitNumberField
            value={minFontSize ?? 8}
            onCommit={(v) => {
              setMinFontSize(v);
              refit();
            }}
            min={1}
          />
        </InspectorRow>
        <InspectorRow label="Max size">
          <CommitNumberField
            value={maxFontSize ?? 256}
            onCommit={(v) => {
              setMaxFontSize(v);
              refit();
            }}
            min={1}
          />
        </InspectorRow>
      </InspectorSection>
      <PlaceholderTransformSection object={object} editor={editor} />
    </>
  );
};

export default PlaceholderTextInspector;

registerInspector('placeholder-text', PlaceholderTextInspector);

const CheckboxLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label
    style={{
      display: 'inline-flex',
      gap: 6,
      alignItems: 'center',
      fontSize: 12,
      color: '#374151',
      cursor: 'pointer',
    }}
  >
    {children}
  </label>
);
